import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { isStandalone } from '../../utils/openFile.js';

// Shows every page of a PDF as a canvas, one below the other. An <iframe> on iOS shows
// only the first page of a PDF, and the bare PDF in the installed app has no way back.
// pdf.js loads only when a PDF is opened; the build copies its worker to /dist/pdfjs/.
//
// `data` is the PDF as an ArrayBuffer, or `src` a URL of the app to fetch it from.
// The component scrolls by itself; give it a height through `style` or its parent.

const PDFJS_DIR = '/dist/pdfjs/';
// Why: iOS refuses canvases above ~16.7M pixels and kills the page when all canvases
// together take too much memory, so pages are drawn small enough and only near the screen.
const MAX_PAGE_PIXELS = 4_000_000;
const NEAR_SCREEN = '1500px 0px';

let pdfjsPromise = null;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf.mjs').then(lib => {
      lib.GlobalWorkerOptions.workerSrc = `${PDFJS_DIR}pdf.worker.min.mjs`;
      return lib;
    }).catch(err => { pdfjsPromise = null; throw err; });
  }
  return pdfjsPromise;
}

export default function PdfPages({ data, src, title = 'PDF', style }) {
  const scroller = useRef(null);
  const [pages, setPages] = useState(null); // [{ page, width, height }]
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let loading = null;
    setPages(null);
    setFailed(false);
    (async () => {
      const bytes = data ?? await fetch(src, { credentials: 'same-origin' }).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      });
      const pdfjs = await loadPdfjs();
      if (cancelled) return;
      // Why a copy: pdf.js transfers the buffer to its worker, which would empty the caller's.
      // Why useWasm: false: the CSP has no 'wasm-unsafe-eval', so scanned images (JPX, JBIG2)
      // are decoded with pdf.js's JavaScript fallback, which the build copies next to it.
      loading = pdfjs.getDocument({ data: bytes.slice(0), useWasm: false, wasmUrl: `${PDFJS_DIR}wasm/` });
      const doc = await loading.promise;
      const list = [];
      for (let n = 1; n <= doc.numPages; n++) {
        const page = await doc.getPage(n);
        const { width, height } = page.getViewport({ scale: 1 });
        list.push({ page, width, height });
      }
      if (!cancelled) setPages(list);
    })().catch(err => {
      if (cancelled) return;
      console.error('PDF could not be shown:', err);
      setFailed(true);
    });
    // Why the loading task: it stops the worker too, also when the PDF is still loading.
    return () => { cancelled = true; loading?.destroy(); };
  }, [data, src]);

  return (
    <div ref={scroller} class="pdf-pages" style={style} role="document" aria-label={title}>
      {failed ? <p class="pdf-pages-message">De PDF kon niet worden geladen.</p>
        : !pages ? <p class="pdf-pages-message">PDF laden…</p>
        : pages.map((p, i) => <PdfPage key={i} {...p} root={scroller} label={`Pagina ${i + 1} van ${pages.length}`} />)}
    </div>
  );
}

// A PDF in a preview modal: an <iframe> in the browser, every page in the installed app.
export function PdfFrame({ src, title, style }) {
  return isStandalone()
    ? <PdfPages src={src} title={title} style={style} />
    : <iframe src={src} title={title} style={style} />;
}

function PdfPage({ page, width, height, root, label }) {
  const box = useRef(null);
  const canvas = useRef(null);

  useEffect(() => {
    let task = null;
    let drawn = false;
    const draw = () => {
      if (drawn) return;
      drawn = true;
      const cssWidth = box.current.clientWidth || width;
      let scale = (cssWidth / width) * Math.min(window.devicePixelRatio || 1, 2);
      scale = Math.min(scale, Math.sqrt(MAX_PAGE_PIXELS / (width * height)));
      const viewport = page.getViewport({ scale });
      const c = canvas.current;
      c.width = Math.floor(viewport.width);
      c.height = Math.floor(viewport.height);
      task = page.render({ canvas: c, viewport });
      task.promise.catch(() => {});
    };
    const clear = () => {
      if (!drawn) return;
      drawn = false;
      task?.cancel();
      // Why 0×0 and cleanup(): frees the canvas and the page's decoded images on iOS,
      // which otherwise pile up for every page scrolled past. The box keeps its height.
      canvas.current.width = 0;
      canvas.current.height = 0;
      page.cleanup();
    };
    if (typeof IntersectionObserver === 'undefined') { draw(); return () => task?.cancel(); }
    const observer = new IntersectionObserver(entries => (entries[entries.length - 1].isIntersecting ? draw() : clear()), { root: root.current, rootMargin: NEAR_SCREEN });
    observer.observe(box.current);
    return () => { observer.disconnect(); task?.cancel(); };
  }, [page]);

  return (
    <div ref={box} class="pdf-page" style={{ aspectRatio: `${width} / ${height}` }}>
      <canvas ref={canvas} aria-label={label} />
    </div>
  );
}
